import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import axios from 'axios';
import { ChevronRight, Layers, Ruler } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERIES_INFO = {
  'laminat': {
    name: 'Laminat',
    brand: 'PARCHET',
    description: 'Parchet laminat de cea mai bună calitate de la KRONOTEX și SUPERIOR. Rezistență, design modern și montaj ușor.',
    image: 'https://images.unsplash.com/photo-1611600700192-d87eaeed4f81?crop=entropy&cs=srgb&fm=jpg&w=600&q=80'
  },
  'spc': {
    name: 'SPC',
    brand: 'PARCHET',
    description: 'Parchet SPC rigid cu miez din piatră. 100% rezistent la apă, ideal pentru orice încăpere.',
    image: 'https://images.unsplash.com/photo-1653972233229-1b8c042d6d8e?crop=entropy&cs=srgb&fm=jpg&w=600&q=80'
  },
  'plinte': {
    name: 'Plinte',
    brand: 'ACCESORII',
    description: 'Plinte din MDF și polimer dur de înaltă calitate pentru finisaje perfecte. Diverse înălțimi și finisaje disponibile.',
    image: 'https://images.unsplash.com/photo-1684419269688-fe48722af68b?w=400&q=70'
  },
  'cornise': {
    name: 'Cornișe',
    brand: 'ACCESORII',
    description: 'Cornișe decorative și funcționale pentru un finisaj perfect. Diverse profile și finisaje disponibile.',
    image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/cdm2z086_2.webp'
  },
  'lemn-stratificat': {
    name: 'Lemn Stratificat',
    brand: 'PARCHET',
    description: 'Parchet din lemn stratificat cu strat superior din lemn natural. Eleganță autentică și durabilitate pentru orice interior.',
    image: 'https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg?auto=compress&w=400&q=70'
  },
  'exquisit-8mm': {
    name: 'Kronotex Exquisit',
    thickness: '8mm',
    brand: 'KRONOTEX',
    description: 'Parchet laminat cu aspect natural și rezistență excelentă la uzură. Perfect pentru living și dormitoare.',
    features: ['Aspect lemn natural', 'AC4 - Utilizare rezidențială', 'Ușor de montat'],
    image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400&q=70'
  },
  'exquisit-plus-8mm': {
    name: 'Kronotex Exquisit Plus',
    thickness: '8mm',
    brand: 'KRONOTEX',
    description: 'Versiunea extinsă a gamei Exquisit cu lamele mai late pentru un aspect modern și spațios.',
    features: ['Format lat 244mm', 'AC4 - Rezidențial', 'Aspect modern'],
    image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400&q=70'
  },
  'amazone-10mm': {
    name: 'Kronotex Amazone',
    thickness: '10mm',
    brand: 'KRONOTEX',
    description: 'Parchet laminat rezistent la apă, ideal pentru bucătării, băi și spații cu umiditate ridicată.',
    features: ['Rezistent la apă', 'AC5 - Trafic intens', 'Garanție 25 ani'],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=70'
  },
  'robusto-12mm': {
    name: 'Kronotex Robusto',
    thickness: '12mm',
    brand: 'KRONOTEX',
    description: 'Parchet laminat extra rezistent pentru trafic intens. Ideal pentru spații comerciale.',
    features: ['Extra rezistent', 'AC5 - Trafic comercial', 'Grosime premium 12mm'],
    image: 'https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg?w=400&q=70'
  },
  'mammut-12mm': {
    name: 'Kronotex Mammut',
    thickness: '12mm',
    brand: 'KRONOTEX',
    description: 'Gama premium cu aspect de lemn masiv și lățime extra. Confort acustic superior.',
    features: ['Aspect lemn masiv', 'Format XXL', 'Confort acustic'],
    image: 'https://images.unsplash.com/photo-1675767443637-f35f2653b17a?w=400&q=70'
  },
  'herringbone-8mm': {
    name: 'Kronotex Herringbone',
    thickness: '8mm',
    brand: 'KRONOTEX',
    description: 'Parchet laminat cu model chevron elegant. Design sofisticat pentru interioare de lux.',
    features: ['Model Herringbone', 'Design elegant', 'Montaj ușor'],
    image: 'https://images.pexels.com/photos/129731/pexels-photo-129731.jpeg?w=400&q=70'
  },
  'mega-plus-8mm': {
    name: 'Kronotex Mega Plus',
    thickness: '8mm',
    brand: 'KRONOTEX',
    description: 'Lamele extra-late de 326mm pentru un aspect grandios. Ideal pentru spații mari deschise.',
    features: ['Format XXL 326mm', 'AC4 - Rezidențial', 'Aspect grandios'],
    image: 'https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg?w=400&q=70'
  },
  'superior': {
    name: 'Superior',
    brand: 'SUPERIOR',
    description: 'Gama completă Superior cu raport calitate-preț excelent. Disponibil în multiple grosimi și finisaje.',
    features: ['Preț accesibil', 'Multiple grosimi', 'Diverse decoruri'],
    image: 'https://images.unsplash.com/photo-1737100907308-67f6b49ba15f?w=400&q=70'
  },
  'advanced-8mm': {
    name: 'Superior Advanced',
    thickness: '8mm',
    brand: 'SUPERIOR',
    description: 'Parchet laminat cu raport calitate-preț excelent. Ideal pentru renovări.',
    features: ['Preț accesibil', 'AC4 - Rezidențial', 'Montaj rapid'],
    image: 'https://images.unsplash.com/photo-1737100907308-67f6b49ba15f?w=400&q=70'
  },
  'progress-10mm': {
    name: 'Superior Progress',
    thickness: '10mm',
    brand: 'SUPERIOR',
    description: 'Parchet laminat 10mm cu durabilitate sporită. Echilibrul perfect între calitate și preț.',
    features: ['Durabilitate sporită', 'AC4+ - Trafic mediu', 'Diverse decoruri'],
    image: 'https://images.unsplash.com/photo-1770982726897-5e85ad0601c3?w=400&q=70'
  },
  'evolution-12mm': {
    name: 'Superior Evolution',
    thickness: '12mm',
    brand: 'SUPERIOR',
    description: 'Gama de top Superior cu grosime 12mm. Confort maxim și aspect premium.',
    features: ['Grosime 12mm', 'Confort superior', 'Aspect premium'],
    image: 'https://images.unsplash.com/photo-1661345699574-85a0c56a184b?w=400&q=70'
  }
};

const LAMINAT_SERIES = ['exquisit-8mm','exquisit-plus-8mm','amazone-10mm','robusto-12mm','mammut-12mm','herringbone-8mm','mega-plus-8mm','superior','advanced-8mm','progress-10mm','evolution-12mm'];

const IMAGE_MAP = {
  'laminat': 'laminat_image', 'spc': 'spc_image', 'plinte': 'plinte_image',
  'exquisit-8mm': 'exquisit_image', 'exquisit-plus-8mm': 'exquisit_plus_image',
  'amazone-10mm': 'amazone_image', 'robusto-12mm': 'robusto_image',
  'mammut-12mm': 'mammut_image', 'herringbone-8mm': 'herringbone_image',
  'mega-plus-8mm': 'mega_plus_image', 'superior': 'superior_image',
  'advanced-8mm': 'advanced_image', 'progress-10mm': 'progress_image', 'evolution-12mm': 'evolution_image',
};
const getImg = (url) => url?.startsWith('/api') ? `${API_URL}${url}` : url;

const ParchetPage = () => {
  const { subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsImages, setCmsImages] = useState({});

  useEffect(() => {
    fetchData();
  }, [subcategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catResponse, cmsRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/pages/parchet`)
      ]);
      setAllCategories(catResponse.data);
      if (cmsRes.data.content) setCmsImages(cmsRes.data.content);

      if (!subcategory || subcategory === 'laminat') {
        const response = await axios.get(`${API_URL}/api/products?category=parchet`);
        setProducts(response.data);
      } else {
        const response = await axios.get(`${API_URL}/api/products?subcategory=${subcategory}`);
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeriesImage = (slug) => {
    // Priority: 1. Category image_url from DB, 2. CMS image, 3. Default from SERIES_INFO
    const cat = allCategories.find(c => c.slug === slug);
    if (cat?.image_url) return getImg(cat.image_url);
    const cmsKey = IMAGE_MAP[slug];
    if (cmsKey && cmsImages[cmsKey]) return getImg(cmsImages[cmsKey]);
    return SERIES_INFO[slug]?.image || '';
  };

  // Determine what level we're at
  const isMainPage = !subcategory;
  const isLaminatPage = subcategory === 'laminat';
  const isSeriesPage = LAMINAT_SERIES.includes(subcategory);
  const isOtherSubPage = subcategory && !isLaminatPage && !isSeriesPage; // spc, plinte

  const currentInfo = subcategory ? SERIES_INFO[subcategory] : null;

  // Categories to show as cards
  const parchetSubs = allCategories.filter(c => c.parent_slug === 'parchet').sort((a, b) => (a.order || 0) - (b.order || 0));
  const laminatSeries = allCategories.filter(c => c.parent_slug === 'laminat').sort((a, b) => (a.order || 0) - (b.order || 0));

  // Product counts by subcategory
  const countBySubcat = {};
  products.forEach(p => {
    const k = p.subcategory_slug || 'other';
    countBySubcat[k] = (countBySubcat[k] || 0) + 1;
  });

  // Breadcrumb
  const renderBreadcrumb = () => (
    <nav className="flex items-center gap-2 text-sm mb-8" data-testid="parchet-breadcrumb">
      <Link to="/" className="text-[#A3A3A3] hover:text-[#D4AF37]">Acasă</Link>
      <ChevronRight size={14} className="text-[#A3A3A3]" />
      <Link to="/parchet" className={!subcategory ? "text-[#D4AF37]" : "text-[#A3A3A3] hover:text-[#D4AF37]"}>
        Parchet
      </Link>
      {isLaminatPage && (
        <>
          <ChevronRight size={14} className="text-[#A3A3A3]" />
          <span className="text-[#D4AF37]">Laminat</span>
        </>
      )}
      {isSeriesPage && currentInfo && (
        <>
          <ChevronRight size={14} className="text-[#A3A3A3]" />
          <Link to="/parchet/laminat" className="text-[#A3A3A3] hover:text-[#D4AF37]">Laminat</Link>
          <ChevronRight size={14} className="text-[#A3A3A3]" />
          <span className="text-[#D4AF37]">{currentInfo.name}</span>
        </>
      )}
      {isOtherSubPage && currentInfo && (
        <>
          <ChevronRight size={14} className="text-[#A3A3A3]" />
          <span className="text-[#D4AF37]">{currentInfo.name}</span>
        </>
      )}
    </nav>
  );

  // Category card component
  const CategoryCard = ({ cat, href, info, count, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Link
        to={href}
        className="block group bg-[#121212] border border-white/5 overflow-hidden hover:border-[#D4AF37]/30 transition-all"
        data-testid={`series-${cat.slug}`}
      >
        <div className="aspect-[16/10] overflow-hidden relative">
          <img
            src={getSeriesImage(cat.slug)}
            alt={cat.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <span className="text-[#D4AF37] text-xs tracking-widest">{info?.brand || ''}</span>
            <h3 className="font-serif text-xl text-white">{cat.name}</h3>
          </div>
          {info?.thickness && (
            <div className="absolute top-4 right-4 bg-[#D4AF37] text-black px-2 py-1 text-xs font-bold">
              {info.thickness}
            </div>
          )}
        </div>
        <div className="p-4">
          <p className="text-[#A3A3A3] text-sm line-clamp-2 mb-3">
            {info?.description || cat.description || ''}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-[#666] text-sm">{count != null ? `${count} produse` : ''}</span>
            <span className="text-[#D4AF37] text-sm group-hover:translate-x-1 transition-transform">
              Vezi colecția &rarr;
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  const parchetSeoTitle = isSeriesPage && currentInfo
    ? `${currentInfo.name} ${currentInfo.thickness || ''} - Parchet Laminat`
    : isLaminatPage ? 'Parchet Laminat - Kronotex & Superior'
    : isOtherSubPage && currentInfo ? `${currentInfo.name} - Parchet`
    : 'Parchet Laminat & SPC';
  const parchetSeoDesc = isSeriesPage && currentInfo
    ? `${currentInfo.description || ''} Descoperă gama ${currentInfo.name} la Shagun Surfaces.`.slice(0, 155)
    : 'Parchet laminat Kronotex și Superior, SPC rezistent la apă. Grosimi 8mm-12mm. Livrare în toată România.';

  return (
    <Layout>
      <SEO
        title={parchetSeoTitle}
        description={parchetSeoDesc}
        canonicalUrl={`https://shagunsurfaces.ro/parchet${subcategory ? '/' + subcategory : ''}`}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#050505]" data-testid="parchet-hero">
        {isMainPage && (
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1611600700192-d87eaeed4f81?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60')` }} />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6">
          {renderBreadcrumb()}

          {/* Series detail header */}
          {(isSeriesPage || isOtherSubPage) && currentInfo ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#D4AF37] text-sm tracking-widest mb-4 block">{currentInfo.brand}</span>
                <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">
                  {currentInfo.name}
                </h1>
                {currentInfo.thickness && (
                  <div className="flex items-center gap-4 mb-6">
                    <span className="bg-[#D4AF37] text-black px-3 py-1 text-sm font-semibold">
                      {currentInfo.thickness}
                    </span>
                  </div>
                )}
                <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">
                  {currentInfo.description}
                </p>
                {currentInfo.features && (
                  <div className="flex flex-wrap gap-3">
                    {currentInfo.features.map((feature, idx) => (
                      <span key={idx} className="bg-[#121212] border border-white/10 px-4 py-2 text-sm text-[#A3A3A3]">
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <img src={getSeriesImage(subcategory)} alt={currentInfo.name} className="w-full aspect-[4/3] object-cover" />
                <div className="absolute bottom-4 right-4 bg-[#D4AF37] text-black px-4 py-2 font-semibold">
                  {products.length} Produse
                </div>
              </div>
            </div>
          ) : isLaminatPage ? (
            <>
              <span className="text-[#D4AF37] text-sm tracking-widest mb-4 block">PARCHET</span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Parchet Laminat
              </h1>
              <p className="text-[#A3A3A3] text-lg max-w-3xl">
                Parchetul laminat KRONOTEX și SUPERIOR poate fi folosit oriunde: în livinguri,
                bucătării și băi, birouri și hoteluri. Alege seria potrivită nevoilor tale.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Parchet
              </h1>
              <p className="text-[#A3A3A3] text-lg max-w-3xl">
                Parchet laminat și SPC de cea mai bună calitate. Structura inovativă a suprafeței
                se apropie de structura lemnului natural, oferind confort și eleganță.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Main page: Laminat, SPC, Plinte */}
      {isMainPage && (
        <section className="py-12 bg-[#0a0a0a]" data-testid="parchet-categories">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-serif text-2xl text-white mb-8">Categorii</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {parchetSubs.map((cat, index) => {
                const info = SERIES_INFO[cat.slug];
                // Count products under this subcategory tree
                let count = 0;
                if (cat.slug === 'laminat') {
                  // Sum all laminat series products
                  LAMINAT_SERIES.forEach(s => { count += countBySubcat[s] || 0; });
                } else {
                  count = countBySubcat[cat.slug] || 0;
                }
                return (
                  <CategoryCard
                    key={cat.slug}
                    cat={cat}
                    href={`/parchet/${cat.slug}`}
                    info={info}
                    count={count}
                    index={index}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Laminat page: Series cards */}
      {isLaminatPage && (
        <section className="py-12 bg-[#0a0a0a]" data-testid="laminat-series">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-serif text-2xl text-white mb-8">Alege Seria</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {laminatSeries.map((cat, index) => {
                const info = SERIES_INFO[cat.slug];
                const count = countBySubcat[cat.slug] || 0;
                return (
                  <CategoryCard
                    key={cat.slug}
                    cat={cat}
                    href={`/parchet/${cat.slug}`}
                    info={info}
                    count={count}
                    index={index}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Products section (for series pages and SPC/Plinte) */}
      {(isSeriesPage || isOtherSubPage) && (
        <section className="py-12 bg-[#050505]" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl text-white">
                {isSeriesPage
                  ? `Produse din seria ${currentInfo?.name || ''}`
                  : `Produse ${currentInfo?.name || ''}`}
              </h2>
              <Link
                to={isSeriesPage ? "/parchet/laminat" : "/parchet"}
                className="text-[#D4AF37] hover:underline text-sm"
              >
                &larr; Înapoi
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-[#121212] aspect-square animate-pulse" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Layers className="text-[#333] mx-auto mb-4" size={48} />
                <p className="text-[#A3A3A3] mb-4">
                  Momentan nu avem produse în această categorie.
                </p>
                <Link to={isSeriesPage ? "/parchet/laminat" : "/parchet"} className="text-[#D4AF37] hover:underline">
                  Înapoi
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* All products preview on main and laminat pages */}
      {(isMainPage || isLaminatPage) && !loading && products.length > 0 && (
        <section className="py-12 bg-[#050505]" data-testid="products-preview">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-serif text-2xl text-white mb-8">
              {isLaminatPage ? 'Toate Produsele Laminat' : 'Produse Recomandate'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.slice(0, 8).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
            {products.length > 8 && (
              <div className="text-center mt-8">
                <p className="text-[#A3A3A3]">
                  {isLaminatPage
                    ? `Afișăm 8 din ${products.length} produse. Alege o serie pentru a vedea toate.`
                    : `${products.length} produse disponibile.`}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Info section on main page */}
      {isMainPage && (
        <section className="py-16 bg-[#0a0a0a] border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <Layers className="text-[#D4AF37] mx-auto mb-4" size={40} />
                <h3 className="font-serif text-xl text-white mb-2">Grosimi Variate</h3>
                <p className="text-[#A3A3A3] text-sm">
                  De la 8mm pentru uz rezidențial până la 12mm pentru trafic comercial intens.
                </p>
              </div>
              <div className="text-center p-6">
                <Ruler className="text-[#D4AF37] mx-auto mb-4" size={40} />
                <h3 className="font-serif text-xl text-white mb-2">Montaj Profesional</h3>
                <p className="text-[#A3A3A3] text-sm">
                  Sistem click pentru montaj rapid și ușor, fără adezivi sau specialiști.
                </p>
              </div>
              <div className="text-center p-6">
                <svg className="text-[#D4AF37] mx-auto mb-4 w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="font-serif text-xl text-white mb-2">Garanție Extinsă</h3>
                <p className="text-[#A3A3A3] text-sm">
                  Garanție de până la 25 de ani pentru uz rezidențial.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ParchetPage;
